import type { TableHTMLAttributes } from 'react';
import { cn } from '../lib/utils';
import { Table } from './table';

export type V2TableProps = TableHTMLAttributes<HTMLTableElement> & {
  /**
   * Give the scroll wrapper a bounded height so a `position: sticky` header
   * inside the table actually sticks.
   *
   * Needed because the wrapper sets `overflow-x: auto`, and CSS computes the
   * other axis from `visible` to `auto` when one axis is not `visible`. That
   * makes the wrapper the nearest scrollport for anything inside it — but with
   * no height limit it never scrolls vertically, so `sticky top-0` has nothing
   * to stick within and silently does nothing. Applying a sticky class to a
   * table in this wrapper without also bounding the height looks correct in
   * review and has no effect in the browser.
   *
   * Opt-in rather than default: bounding the height changes the page rhythm of
   * every table that uses this component, and most of them are short enough
   * that a frozen header would cost more room than it returns.
   */
  stickyHeaderViewport?: boolean;
};

export function V2Table({ className, stickyHeaderViewport, ...props }: V2TableProps) {
  return (
    <div
      className={cn(
        'max-w-full overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm',
        // `overflow-auto` (both axes) plus a height cap is what turns this into
        // a real vertical scrollport. Matches the cap the week matrix already
        // uses for the same purpose.
        stickyHeaderViewport && 'max-h-[72vh] overflow-auto overscroll-contain',
      )}
    >
      {/* Merge note: main added the sticky-header viewport wrapper; R5 moved the
          element to the canonical `Table` root. Both are kept - the wrapper is
          what makes the documented `stickyHeaderViewport` prop actually work,
          and `Table` forwards className and the rest of props to its own
          `<table>`, so the data attribute and classes below still land. */}
      <Table
        {...props}
        data-v2-table="true"
        className={cn('statura-data-table w-full min-w-full border-collapse text-sm', className)}
      />
    </div>
  );
}
